import * as actionTypes from "../constants/userConstants";

const VALID_ROLES = [
  "user",
  "customer",
  "admin",
  "super_admin",
  "store_manager",
  "editor",
];

function normalizeRoles(value) {
  const rawRoles = Array.isArray(value)
    ? value
    : String(value || "")
        .split(",")
        .map((role) => role.trim());

  const roles = [
    ...new Set(
      rawRoles
        .map((role) => String(role).trim().toLowerCase())
        .filter((role) => VALID_ROLES.includes(role)),
    ),
  ];

  return roles.length ? roles : ["user"];
}

function normalizeUser(user) {
  if (!user) return null;

  const defaultShippingAddress =
    user.defaultShippingAddress || user.checkoutDefaults?.shippingAddress || {};

  return {
    ...user,
    title: user.title || "",
    name: user.name || "",
    lastName: user.lastName || "",
    roles: normalizeRoles(user.roles ?? user.role),
    defaultShippingAddress,
    checkoutDefaults: {
      ...(user.checkoutDefaults || {}),
      shippingAddress: defaultShippingAddress,
    },
  };
}

const storedUser = (() => {
  try {
    if (typeof window === "undefined") return null;

    const raw = localStorage.getItem("userInfo");
    if (!raw) return null;

    return normalizeUser(JSON.parse(raw));
  } catch {
    return null;
  }
})();

export const userAuthReducer = (
  state = {
    loading: false,

    // If a user exists in localStorage, verify it with /check-session first.
    checked: !storedUser,

    userInfo: storedUser,
    isAuthenticated: !!storedUser,
    error: null,
  },
  action,
) => {
  switch (action.type) {
    case actionTypes.USER_AUTH_REQUEST:
      return {
        ...state,
        loading: true,
        checked: false,
        error: null,
      };

    case actionTypes.USER_AUTH_SUCCESS:
    case actionTypes.USER_DEFAULT_SHIPPING_ADDRESS_UPDATE_SUCCESS:
      return {
        loading: false,
        checked: true,
        userInfo: normalizeUser(action.payload),
        isAuthenticated: true,
        error: null,
      };

    case actionTypes.USER_AUTH_FAIL:
      return {
        loading: false,
        checked: true,
        userInfo: null,
        isAuthenticated: false,
        error: action.payload,
      };

    case actionTypes.USER_AUTH_RESET:
    case actionTypes.USER_LOGOUT:
      return {
        loading: false,
        checked: true,
        userInfo: null,
        isAuthenticated: false,
        error: null,
      };

    default:
      return state;
  }
};

export const userLoginReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_LOGIN_REQUEST:
      return { loading: true };

    case actionTypes.USER_LOGIN_SUCCESS:
      return { loading: false, success: true };

    case actionTypes.USER_LOGIN_FAIL:
      return { loading: false, error: action.payload };

    case actionTypes.USER_LOGIN_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userRegisterReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_REGISTER_REQUEST:
      return { loading: true, fieldErrors: {} };

    case actionTypes.USER_REGISTER_SUCCESS:
      return {
        loading: false,
        success: true,
        code: action.payload?.code || "",
        message: action.payload?.message || "",
        verificationRequired: Boolean(action.payload?.verificationRequired),
        email: action.payload?.email || "",
        user: normalizeUser(action.payload?.user),
        fieldErrors: {},
      };

    case actionTypes.USER_REGISTER_FAIL:
      return {
        loading: false,
        error: action.payload,
        code: action.payload?.code || "",
        verificationRequired: Boolean(action.payload?.verificationRequired),
        email: action.payload?.email || "",
        fieldErrors: action.payload?.fieldErrors || {},
      };

    case actionTypes.USER_REGISTER_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userEmailVerifyReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_EMAIL_VERIFY_REQUEST:
      return { loading: true };

    case actionTypes.USER_EMAIL_VERIFY_SUCCESS:
      return {
        loading: false,
        success: true,
        message: action.payload?.message || "",
        user: normalizeUser(action.payload?.user),
      };

    case actionTypes.USER_EMAIL_VERIFY_FAIL:
      return { loading: false, error: action.payload };

    case actionTypes.USER_EMAIL_VERIFY_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userEmailResendReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_EMAIL_RESEND_REQUEST:
      return { loading: true };

    case actionTypes.USER_EMAIL_RESEND_SUCCESS:
      return {
        loading: false,
        success: true,
        message: action.payload,
      };

    case actionTypes.USER_EMAIL_RESEND_FAIL:
      return {
        loading: false,
        error: action.payload,
        code: action.payload?.code || "",
      };

    case actionTypes.USER_EMAIL_RESEND_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userUpdateProfileReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_PROFILE_UPDATE_REQUEST:
      return { loading: true };

    case actionTypes.USER_PROFILE_UPDATE_SUCCESS:
      return {
        loading: false,
        success: true,
        user: normalizeUser(action.payload),
      };

    case actionTypes.USER_PROFILE_UPDATE_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.USER_PROFILE_UPDATE_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userEmailChangeRequestReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_EMAIL_CHANGE_REQUEST_REQUEST:
      return { loading: true };

    case actionTypes.USER_EMAIL_CHANGE_REQUEST_SUCCESS:
      return {
        loading: false,
        success: true,
        message: action.payload?.message || "",
        pendingEmail: action.payload?.pendingEmail || "",
      };

    case actionTypes.USER_EMAIL_CHANGE_REQUEST_FAIL:
      return { loading: false, error: action.payload };

    case actionTypes.USER_EMAIL_CHANGE_REQUEST_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userEmailChangeVerifyReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_EMAIL_CHANGE_VERIFY_REQUEST:
      return { loading: true };

    case actionTypes.USER_EMAIL_CHANGE_VERIFY_SUCCESS:
      return {
        loading: false,
        success: true,
        message: action.payload?.message || "",
        user: normalizeUser(action.payload?.user),
      };

    case actionTypes.USER_EMAIL_CHANGE_VERIFY_FAIL:
      return { loading: false, error: action.payload };

    case actionTypes.USER_EMAIL_CHANGE_VERIFY_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userForgotPasswordReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_FORGOT_PASSWORD_REQUEST:
      return { loading: true };

    case actionTypes.USER_FORGOT_PASSWORD_SUCCESS:
      return {
        loading: false,
        success: true,
        message: action.payload,
      };

    case actionTypes.USER_FORGOT_PASSWORD_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.USER_FORGOT_PASSWORD_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userResetPasswordReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.USER_RESET_PASSWORD_REQUEST:
      return { loading: true };

    case actionTypes.USER_RESET_PASSWORD_SUCCESS:
      return {
        loading: false,
        success: true,
      };

    case actionTypes.USER_RESET_PASSWORD_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.USER_RESET_PASSWORD_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const adminUserListReducer = (
  state = {
    users: [],
    total: 0,
    page: 1,
    pages: 1,
  },
  action,
) => {
  switch (action.type) {
    case actionTypes.ADMIN_USER_LIST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.ADMIN_USER_LIST_SUCCESS:
      return {
        loading: false,
        users: Array.isArray(action.payload?.users)
          ? action.payload.users.map(normalizeUser).filter(Boolean)
          : [],
        total: action.payload?.total || 0,
        page: action.payload?.page || 1,
        pages: action.payload?.pages || 1,
        error: null,
      };

    case actionTypes.ADMIN_USER_LIST_FAIL:
      return {
        loading: false,
        users: [],
        total: 0,
        page: 1,
        pages: 1,
        error: action.payload,
      };

    case actionTypes.ADMIN_USER_LIST_RESET:
    case actionTypes.USER_LOGOUT:
      return {
        users: [],
        total: 0,
        page: 1,
        pages: 1,
      };

    default:
      return state;
  }
};

export const adminUserDetailsReducer = (state = { user: null }, action) => {
  switch (action.type) {
    case actionTypes.ADMIN_USER_DETAILS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.ADMIN_USER_DETAILS_SUCCESS:
      return {
        loading: false,
        user: normalizeUser(action.payload),
        error: null,
      };

    case actionTypes.ADMIN_USER_DETAILS_FAIL:
      return {
        loading: false,
        user: null,
        error: action.payload,
      };

    case actionTypes.ADMIN_USER_DETAILS_RESET:
    case actionTypes.USER_LOGOUT:
      return { user: null };

    default:
      return state;
  }
};

export const adminUserCreateReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.ADMIN_USER_CREATE_REQUEST:
      return { loading: true };

    case actionTypes.ADMIN_USER_CREATE_SUCCESS:
      return {
        loading: false,
        success: true,
        user: normalizeUser(action.payload),
      };

    case actionTypes.ADMIN_USER_CREATE_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.ADMIN_USER_CREATE_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const adminUserUpdateReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.ADMIN_USER_UPDATE_REQUEST:
      return { loading: true };

    case actionTypes.ADMIN_USER_UPDATE_SUCCESS:
      return {
        loading: false,
        success: true,
        user: normalizeUser(action.payload),
      };

    case actionTypes.ADMIN_USER_UPDATE_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.ADMIN_USER_UPDATE_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const adminUserDeleteReducer = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.ADMIN_USER_DELETE_REQUEST:
      return { loading: true };

    case actionTypes.ADMIN_USER_DELETE_SUCCESS:
      return {
        loading: false,
        success: true,
        deletedId: action.payload,
      };

    case actionTypes.ADMIN_USER_DELETE_FAIL:
      return {
        loading: false,
        error: action.payload,
      };

    case actionTypes.ADMIN_USER_DELETE_RESET:
    case actionTypes.USER_LOGOUT:
      return {};

    default:
      return state;
  }
};
